import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiError, downloadFile } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";

const STATI = ["RICHIESTA RICEVUTA", "IN LAVORAZIONE", "APPROVATA", "COMPLETATA", "ANNULLATA"];
const STATI_PAGAMENTO = ["PAGAMENTO IN ATTESA", "PAGAMENTO RICEVUTO", "PAGAMENTO VERIFICATO", "PAGAMENTO NON VERIFICATO"];

function RichiestaCard({ r, isOpen, onToggle, onCambiaStato, onCambiaStatoPagamento, onScaricaXml, onUploadCertificato }) {
  const [certFile, setCertFile] = useState(null);
  const campi = [
    ["Artista", r.nome_cognome],
    ["Codice Fiscale", r.codice_fiscale],
    ["Email artista", r.email_artista],
    ["Locale", r.nome_locale],
    ["Indirizzo", r.indirizzo_locale],
    ["Città", r.citta],
    ["Provincia", r.provincia],
    ["CAP", r.cap],
    ["Attività", r.attivita],
    ["Data esibizione", new Date(r.data_esibizione).toLocaleDateString("it-IT")],
    ["Importo", `€ ${r.importo.toFixed(2)}`],
    ["Email certificato", r.email_certificato],
    ["Data richiesta", new Date(r.created_at).toLocaleString("it-IT")],
  ];

  return (
    <div className="card-elegant !p-5" data-testid={`admin-richiesta-${r.numero_pratica}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading font-bold text-bordeaux-dark">{r.numero_pratica}</p>
          <p className="text-sm text-muted-foreground">
            {r.nome_cognome} · {r.nome_locale} · {new Date(r.data_esibizione).toLocaleDateString("it-IT")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge stato={r.stato} />
          <button
            onClick={onToggle}
            data-testid={`admin-richiesta-toggle-${r.numero_pratica}`}
            className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-bordeaux"
            aria-label="Dettagli"
          >
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-4 border-t border-border pt-4" data-testid={`admin-richiesta-detail-${r.numero_pratica}`}>
          <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {campi.map(([k, v]) => (
              <div key={k}><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
          </dl>
          {r.pagamento && (
            <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4" data-testid={`admin-pagamento-${r.numero_pratica}`}>
              <p className="text-sm font-semibold text-bordeaux-dark">Pagamento</p>
              <dl className="mt-2 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div><dt className="text-muted-foreground">Importo</dt><dd className="font-medium">€ {r.pagamento.importo?.toFixed(2)}</dd></div>
                <div><dt className="text-muted-foreground">Metodo</dt><dd className="font-medium">{r.pagamento.metodo === "paypal" ? "PayPal" : "Bonifico bancario"}</dd></div>
                <div><dt className="text-muted-foreground">Stato pagamento</dt><dd><StatusBadge stato={r.pagamento.stato} /></dd></div>
                {r.pagamento.data && (
                  <div><dt className="text-muted-foreground">Data pagamento</dt><dd className="font-medium">{new Date(r.pagamento.data).toLocaleString("it-IT")}</dd></div>
                )}
                {r.pagamento.transazione_id && (
                  <div><dt className="text-muted-foreground">ID transazione PayPal</dt><dd className="font-medium">{r.pagamento.transazione_id}</dd></div>
                )}
              </dl>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium" htmlFor={`pag-${r.id}`}>Modifica stato pagamento:</label>
                <select
                  id={`pag-${r.id}`}
                  data-testid={`admin-pagamento-select-${r.numero_pratica}`}
                  className="input-elegant !w-auto"
                  value={r.pagamento.stato}
                  onChange={(e) => onCambiaStatoPagamento(r, e.target.value)}
                >
                  {STATI_PAGAMENTO.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm font-medium" htmlFor={`stato-${r.id}`}>Modifica stato:</label>
            <select
              id={`stato-${r.id}`}
              data-testid={`admin-stato-select-${r.numero_pratica}`}
              className="input-elegant !w-auto"
              value={r.stato}
              onChange={(e) => onCambiaStato(r, e.target.value)}
            >
              {STATI.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-4" data-testid={`admin-inps-${r.numero_pratica}`}>
            <button onClick={() => onScaricaXml(r)} data-testid={`admin-xml-btn-${r.numero_pratica}`} className="btn-outline-bordeaux !px-4 !py-2 text-xs">
              SCARICA XML PER INPS
            </button>
            <input
              type="file"
              accept="application/pdf"
              data-testid={`admin-certificato-input-${r.numero_pratica}`}
              className="text-xs text-muted-foreground"
              onChange={(e) => setCertFile(e.target.files?.[0] || null)}
            />
            <button
              onClick={() => onUploadCertificato(r, certFile)}
              disabled={!certFile}
              data-testid={`admin-certificato-upload-btn-${r.numero_pratica}`}
              className="btn-bordeaux !px-4 !py-2 text-xs"
            >
              CARICA CERTIFICATO INPS
            </button>
            {r.certificato_disponibile && (
              <span className="text-xs font-semibold text-green-700" data-testid={`admin-certificato-ok-${r.numero_pratica}`}>
                Certificato presente{r.certificato_data ? ` dal ${new Date(r.certificato_data).toLocaleDateString("it-IT")}` : ""}
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Flusso INPS: scarica l'XML, caricalo sul portale INPS (Upload massivo), poi carica qui il PDF del certificato ufficiale rilasciato dall'INPS: l'artista lo troverà nella sua Area Personale e la pratica passerà a COMPLETATA.
          </p>
        </div>
      )}
    </div>
  );
}

export default function AdminRichieste() {
  const [richieste, setRichieste] = useState([]);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const [aperta, setAperta] = useState(null);

  const carica = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (stato) params.stato = stato;
    api.get("/admin/richieste", { params }).then((res) => setRichieste(res.data)).catch(() => {});
  }, [q, stato]);

  useEffect(() => {
    const t = setTimeout(carica, 300);
    return () => clearTimeout(t);
  }, [carica]);

  const cambiaStato = async (r, nuovoStato) => {
    try {
      await api.patch(`/admin/richieste/${r.id}/stato`, { stato: nuovoStato });
      toast.success(`Pratica ${r.numero_pratica} aggiornata: ${nuovoStato}`);
      carica();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cambiaStatoPagamento = async (r, nuovoStato) => {
    try {
      await api.patch(`/admin/richieste/${r.id}/pagamento`, { stato_pagamento: nuovoStato });
      toast.success(`Pagamento ${r.numero_pratica}: ${nuovoStato}`);
      carica();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const scaricaXml = async (r) => {
    try {
      await downloadFile(`/admin/richieste/${r.id}/xml`, `agibilita-${r.numero_pratica}.xml`);
    } catch {
      toast.error("Download XML non riuscito");
    }
  };

  const uploadCertificato = async (r, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/richieste/${r.id}/certificato`, fd);
      toast.success(`Certificato caricato per ${r.numero_pratica}: pratica COMPLETATA`);
      carica();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="admin-richieste-page">
      <h1 className="font-heading text-3xl font-bold text-bordeaux-dark">Richieste di Agibilità</h1>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="admin-richieste-search"
            className="input-elegant !pl-9"
            placeholder="Cerca per pratica, artista, locale o codice fiscale..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="admin-richieste-stato-filter"
          className="input-elegant !w-auto"
          value={stato}
          onChange={(e) => setStato(e.target.value)}
        >
          <option value="">Tutti gli stati</option>
          {STATI.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 space-y-3">
        {richieste.length === 0 && (
          <p className="card-elegant text-center text-sm text-muted-foreground" data-testid="admin-richieste-empty">Nessuna richiesta trovata.</p>
        )}
        {richieste.map((r) => (
          <RichiestaCard
            key={r.id}
            r={r}
            isOpen={aperta === r.id}
            onToggle={() => setAperta(aperta === r.id ? null : r.id)}
            onCambiaStato={cambiaStato}
            onCambiaStatoPagamento={cambiaStatoPagamento}
          />
        ))}
      </div>
    </div>
  );
}
